import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  AddButton,
  CustomConfirmModal,
  PaginationDropdown,
  ThinDropdownWithSearch,
} from "../../common/GeneralComponent";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Pagination,
} from "@mui/material";
import DataTable from "react-data-table-component";
import { fetchData } from "../../../utils/functions";
import {
  ChevronRight,
  ChevronsRightIcon,
  Edit,
  Minus,
  Plus,
  Trash,
  Download,
} from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "font-awesome/css/font-awesome.min.css";
import * as XLSX from "xlsx";
import GeneralFooter from "../../common/GeneralFooter";
import PermissionButton from "../../common/PermissionButton";
//import { setPage, setSubpage } from "../../../slices/breadCrumbSlice";

const Roles = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [groups, setGroups] = useState();
  const [rolesData, setRolesData] = useState([]);
  const [activeRoles, setActiveRoles] = useState([]); // Active roles for dropdown
  const [groupOptions, setGroupOptions] = useState([]);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);

  const [selectedRole, setSelectedRole] = useState(null);
  const [search, setSearch] = useState("");
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState({
     currentPage: 1,
     pageSize: 10,
     totalItems: 0,
     totalPages: 0,
   });
    const { user: loggedInUser } = useSelector((state) => state.auth);
    const permissions = loggedInUser?.user_permissions || [];

  const fetchRoles = async () => {
    try {
      const queryParams = {
        search: search || undefined,
        page: pagination.currentPage,
        limit: pagination.pageSize,
      };

      if (groups) queryParams.groupFilter = groups;
      if (selectedRole) queryParams.roleFilter = selectedRole;

      const response = await fetchData(
        "role",
        "GET",
        null,
        {},
        queryParams,
        navigate,
        "/role",
        dispatch
      );

      const rolesList = (response?.data || []).reverse();
      setRolesData(rolesList);
      // setPagination(response?.pagination || {});
         setPagination((prev) => ({
           ...prev,
           currentPage: response?.pagination?.currentPage || prev.currentPage,
           totalItems: response?.pagination?.totalItems || 0,
           totalPages: response?.pagination?.totalPages || 0,
         }));

    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };


  const exportToExcel = async () => {
    try {
      setIsExporting(true);
      const response = await fetchData(
        "role",
        "GET",
        null,
        {},
        { search: search || undefined, page: 1, limit: 10000 },
        navigate,
        "/role",
        dispatch
      );

      const allRoles = response?.data || [];

      if (!allRoles.length) {
        toast.warning("No roles to export");
        return;
      }

      const excelData = allRoles.map((role, index) => ({
        "Sr. No": index + 1,
        Role: role.role?.role_name || "",
        Group: role.role?.groupName || "",
        Status: role.role?.status === 1 ? "Active" : "Inactive",
        "Created At": role.role?.createdAt
          ? new Date(role.role.createdAt).toLocaleDateString()
          : "",
        "Updated At": role.role?.updatedAt
          ? new Date(role.role.updatedAt).toLocaleDateString()
          : "",
        "Permissions": role.role?.Permissions 
          ? role.role.Permissions.map(p => p.permission_name).join(", ")
          : "",
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Roles");

      XLSX.writeFile(
        workbook,
        // `roles_export_${new Date().toISOString().split("T")[0]}.xlsx`
        `roles_export_${new Date().toISOString().split("T")[0]}.csv`,
      );
      toast.success(`Successfully exported ${allRoles.length} roles to Excel`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to export roles to Excel");
    } finally {
      setIsExporting(false);
    }
  };

  // Fetch active roles (dropdown data)
  const fetchActiveRoles = async () => {
    try {
      const response = await fetchData(
        "role",
        "GET",
        null,
        {},
        { search: "active" }, // Fetch only active roles
        navigate,
        "/role",
        dispatch
      );
      setActiveRoles(
        response?.data?.map((role) => ({
          value: role.role.id,
          label: role.role.role_name,
        })) || []
      );
    } catch (error) {
      console.error("Error fetching active roles:", error);
    }
  };

  // Fetch groups for dropdown
  const fetchGroups = async () => {
    try {
      const response = await fetchData(
        "groups",
        "GET",
        null,
        {},
        {},
        navigate,
        "/groups",
        dispatch
      );
      setGroupOptions(
        response?.data?.map((group) => ({
          value: group.id,
          label: group.name,
        })) || []
      );
    } catch (error) {
      console.error("Error fetching groups:", error);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchActiveRoles();
    fetchGroups();
  }, [
    pagination.currentPage,
    pagination.pageSize,
    search,
    groups,
    selectedRole,
  ]);

  const handleGroupChange = (selectedGroup) => {
    setGroups(selectedGroup);
    setSelectedRole();
  };

  const handleRoleChange = (selectedRole) => {
    setSelectedRole(selectedRole);
    // rolesData();
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
  };

  const handleDelete = async () => {
    setModalVisible(false);
    setRolesData((prev) =>
      prev.filter((role) => role.role.id !== selectedUuid)
    );
    try {
      const response = await fetchData(
        `role`,
        "DELETE",
        { id: selectedUuid },
        {},
        {},
        navigate,
        "/role",
        dispatch
      );
      if (!response?.success) {
        throw new Error(response?.message || "Failed to delete the role.");
      }
      toast.success("Role deleted successfully.");
    } catch (error) {
      console.error("Error deleting role:", error);
      toast.error(
        error?.message || "An error occurred while deleting the role."
      );
      fetchRoles(); // Refresh roles
    }
  };

  const showDeleteModal = (id) => {
    setSelectedUuid(id);
    setModalVisible(true);
  };

  const columns = [
    {
      name: <span className="text-[0.819rem] uppercase">Role</span>,
      selector: (row) => row.role?.role_name || "N/A",
      sortable: true,
    },
    {
      name: <span className="text-[0.819rem] uppercase">Group</span>,
      selector: (row) => row.role?.groupName || "N/A",
      sortable: true,
    },
    {
      name: <span className="text-[0.819rem] uppercase">Status</span>,
      cell: (row) => (
        <span
          className={`py-1 px-2 text-[0.85em] rounded ${
            row.role?.status === 1
              ? "bg-success text-white"
              : "bg-red-500 text-white"
          }`}
        >
          {row.role?.status === 0 ? "Inactive" : "Active"}
        </span>
      ),
    },
  ];
  {
    if (
      permissions.includes("role-update") ||
      permissions.includes("role-delete")
    ) {
      columns.push({
        name: <span className="text-[0.819rem] uppercase">Action</span>,
        cell: (row) => (
          <div className="flex gap-2">
            {/* <Link>

<i class="fa-solid fa-eye text-primaryDark fa-lg"></i>
        </Link> */}
            {/* <Link to={`/roles/edit/${row.role?.id}`}>
          <i class="fa-solid fa-pen-to-square fa-lg"></i>
        </Link> */}

            {/* <button onClick={() => showDeleteModal(row.role?.id)}>
          <i
            class="fa fa-trash fa-lg text-primaryDark"
            aria-hidden="true"
          ></i> */}
            {/* <Trash
            size={30}
            strokeWidth={1.5}
            color="rgb(230, 83, 60)"
            className="transition-colors duration-300 hover:stroke-current hover:bg-red-600  rounded-md bg-bgDanger p-2 hover:text-white"
          />  */}
            {/* </button> */}
            <PermissionButton
              hasPermission={permissions.includes("role-update")}
              // faIcon="fa-solid fa-pen-to-square fa-lg"
              // onlyIcon
              to={`/roles/edit/${row.role?.id}`}
              faIcon="fa-solid fa-pen-to-square fa-lg"
              onlyIcon={true}
              // className="text-primaryDark hover:text-[#137e81]" // keeps it like your <a>
              enabledClass="" // no background
              disabledClass="opacity-50 cursor-not-allowed"
            />
            <PermissionButton
              hasPermission={permissions.includes("role-delete")}
              faIcon="fa fa-trash fa-lg text-primaryDark"
              onlyIcon
              onClick={() => showDeleteModal(row.role?.id)}
              className="p-2 rounded-full bg-gray-200 text-[#137e81] hover:text-white"
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed bg-gray-300"
            />

            <CustomConfirmModal
              isVisible={isModalVisible}
              onConfirm={handleDelete}
              onCancel={() => setModalVisible(false)}
              message="Are you sure you want to delete this Role?"
            />
          </div>
        ),
      });
    }
}

  {
    /*} useEffect(() => {
    dispatch(setPage("Roles"));
    dispatch(setSubpage(undefined));
  }, [dispatch]);*/
  }

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        backgroundColor: "#fff",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  return (
    <div className="my-4 w-full flex flex-col justify-between items-center">
      <div className="flex  lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Manage Roles
        </h1>
        <div className="flex gap-3">
          <button
            onClick={exportToExcel}
            disabled={isExporting}
            className={`flex items-center gap-2 px-4 py-1 text-sm bg-[#137e81] hover:bg-green-700 text-white rounded-md transition-colors ${
              isExporting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <Download size={16} />
            {isExporting ? "Exporting..." : "Export Excel"}
          </button>
          {/* <Link to="add">
            <AddButton text="Add" icon={ChevronsRightIcon} />
          </Link> */}
          <PermissionButton
            hasPermission={permissions.includes("role-create")}
            to="add"
            faIcon="fa fa-plus"
            text="Add"
            className="flex items-center gap-2 py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg:text-[15px] text-[12px]"
            enabledClass="bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85"
            disabledClass="bg-[#be9609] text-white opacity-50 cursor-not-allowed"
          />

        </div>
      </div>
      {/* <div className="bg-white rounded-lg shadow w-full">
        <Accordion>
          <AccordionSummary
            expandIcon={
              isFiltersVisible ? (
                <Minus color="#ffff" size={14} />
              ) : (
                <Plus size={14} color="#ffff" />
              )
            }
            onClick={toggleFilters}
          >
            Filters
          </AccordionSummary>
          <AccordionDetails>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 w-full">
              <ThinDropdownWithSearch required={false}
                label="Group"
                options={groupOptions}
                value={groups}
                onChange={handleGroupChange}
                required={false}
              />
              <ThinDropdownWithSearch required={false}
                label="Roles"
                options={
                  rolesData.map((role) => ({
                    value: role.role.id,
                    label: role.role.role_name,
                  })) || []
                } // Active roles for dropdown
                value={selectedRole}
                onChange={handleRoleChange}
                required={false}
              />
            </div>
          </AccordionDetails>
        </Accordion>
      </div> */}
      <div className="rounded-lg mb-[30px] mt-4 shadow w-full bg-white">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b ">
          <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Roles List
          </h1>
        </div>
        <div className="p-[1.25rem] w-full">
          <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-[6px] w-full">
            <PaginationDropdown
              value={pagination.pageSize}
              onChange={handlePageSizeChange}
              options={[
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 30, label: "30" },
              ]}
            />

            <input
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination((prev) => ({ ...prev, currentPage: 1 }));
              }}
              placeholder="Search..."
              type="search"
              className="font-[Light] text-[#212529] text-[14px] flex items-center bg-[#f1f4f8] border-[#ebeef2] border-[1px] rounded-md px-3 py-1 outline-none"
            />
          </div>
          <DataTable
            columns={columns}
            data={rolesData}
            customStyles={customStyles}
            highlightOnHover
          />
          <GeneralFooter
            pagination={pagination}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            dataLength={rolesData.length}
          />
        </div>
      </div>
    </div>
  );
};

export default Roles;